import { useEffect, useState } from "react";
import type { User } from "../lib/types";
import { authClient } from "../lib/auth-client";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { I } from "../components/Icons";

interface Props {
  user: User;
  onSignOut: () => void;
}

export function Settings({ user, onSignOut }: Props) {
  return (
    <div className="gf-shell">
      <Sidebar active="settings" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "Settings", strong: true },
          ]}
        />
        <div className="gf-dash">
          <div className="gf-dash__section-head">
            <div>
              <h2>Settings</h2>
              <p>Update how your name shows up across Git&nbsp;Fit.</p>
            </div>
          </div>

          <ProfileCard user={user} />
          <AccountCard user={user} onSignOut={onSignOut} />
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ user }: { user: User }) {
  const [name, setName] = useState(user.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Keep the field in sync if the session name changes underneath us.
  useEffect(() => {
    setName(user.name);
  }, [user.name]);

  const trimmed = name.trim();
  const dirty = trimmed.length > 0 && trimmed !== user.name;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || busy) return;
    setErr(null);
    setOk(false);
    setBusy(true);
    try {
      // better-auth persists the change and refreshes the session, so the
      // sidebar/topbar pick up the new name automatically.
      const res = await authClient.updateUser({ name: trimmed });
      if ((res as { error?: { message?: string } }).error) {
        setErr(
          (res as { error?: { message?: string } }).error?.message ??
            "Couldn't save your name.",
        );
        return;
      }
      setOk(true);
    } catch {
      setErr("Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="gf-card" style={{ padding: "var(--space-5)" }} onSubmit={submit}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <I.Settings size={16} />
        <h3 style={{ margin: 0, fontSize: "var(--fs-h4)" }}>Profile</h3>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: "var(--fs-body-sm)", color: "var(--fg-2)" }}>
        Your display name appears in the sidebar, on the dashboard, and to your
        instructor in the team progress view.
      </p>

      <label className="gf-field" style={{ maxWidth: 340 }}>
        <span>Display name</span>
        <input
          className="gf-input"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setOk(false);
            setErr(null);
          }}
          placeholder="Your name"
          autoComplete="name"
          required
        />
      </label>

      <label className="gf-field" style={{ maxWidth: 340, marginTop: 12 }}>
        <span>Email</span>
        <input className="gf-input" value={user.email} disabled readOnly />
      </label>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
        <button type="submit" className="gf-btn gf-btn--gradient" disabled={!dirty || busy}>
          {busy ? "Saving…" : <>
            <I.Save size={14} /> Save changes
          </>}
        </button>
        {ok && (
          <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--success)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <I.Check size={14} /> Saved
          </span>
        )}
        {err && (
          <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--danger)" }}>{err}</span>
        )}
      </div>
    </form>
  );
}

function AccountCard({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  return (
    <div className="gf-card" style={{ padding: "var(--space-5)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <I.Eye size={16} />
        <h3 style={{ margin: 0, fontSize: "var(--fs-h4)" }}>Account</h3>
      </div>

      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "8px 24px",
          margin: 0,
          fontSize: "var(--fs-body-sm)",
        }}
      >
        <dt style={{ color: "var(--fg-2)" }}>Role</dt>
        <dd style={{ margin: 0 }}>{user.isAdmin ? "Admin" : "Learner"}</dd>
        <dt style={{ color: "var(--fg-2)" }}>Job title</dt>
        <dd style={{ margin: 0 }}>{user.role}</dd>
      </dl>

      <button
        type="button"
        className="gf-btn gf-btn--secondary"
        style={{ marginTop: 20 }}
        onClick={onSignOut}
      >
        <I.X size={14} /> Sign out
      </button>
    </div>
  );
}
